import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {differentOrNull} from '../../shared/helper/util';
import * as QuoteActions from '../actions/quote.action';
import * as QuotesActions from '../actions/quotes.actions';
import {Quote} from '../model/quote';
import {PendingQuoteCollection} from '../model/quote-bar-item';
import {Action, createReducer, on} from '@ngrx/store';

export interface State extends EntityState<Quote> {
    preferredQuoteId: number;
    selectedQuoteId: number;
    isPending: boolean;
    pendingQuoteCollection: PendingQuoteCollection;
    addToQuoteNav: boolean;
    addToQuoteMenu: boolean;
    createNewQuote: boolean;
    chooseQuote: boolean;
    chosenQuote: Quote;
    quoteListMenu: boolean;
    quoteEdit: boolean;
    requestQuote: boolean;
    quoteDetail: boolean;
    quoteOrderComplete: boolean;
    selectedQuantity: number;
    newlyCreatedQuote: Quote;
}

export const adapter: EntityAdapter<Quote> = createEntityAdapter<Quote>({
    selectId: (quote) => quote.id,
});

export const initialState: State = adapter.getInitialState({
    preferredQuoteId: null,
    selectedQuoteId: null,
    isPending: false,
    pendingQuoteCollection: [],
    addToQuoteNav: false,
    addToQuoteMenu: false,
    createNewQuote: false,
    chooseQuote: false,
    chosenQuote: null,
    quoteListMenu: false,
    quoteEdit: false,
    requestQuote: false,
    quoteDetail: false,
    quoteOrderComplete: false,
    selectedQuantity: 1,
    newlyCreatedQuote: null
});

export function reducer(state = initialState, action: Action): State {
    return {
        ...entityReducer(state, action),
        preferredQuoteId: determinePreferredQuoteId(state.preferredQuoteId, action),
        selectedQuoteId: determineSelectedQuoteId(state.selectedQuoteId, action),
        isPending: determineIsPending(state.isPending, action),
        pendingQuoteCollection: determinePendingQuoteCollection(state.pendingQuoteCollection, action)
    };
}

const entityReducer = createReducer(
    initialState,
    on(QuotesActions.loadingSuccess, ((state, {quotes}) => {
        return adapter.addMany(quotes, state);
    })),
    on(QuoteActions.createQuoteSuccess, ((state, {quote}) => {
        return adapter.addOne(quote, state);
    })),
    on(
        QuoteActions.editQuoteSuccess,
        QuoteActions.addProductSuccess,
        QuoteActions.editProductSuccess,
        QuoteActions.deleteProductSuccess,
        QuoteActions.requestSuccess, ((state, {quote}) => {
           return adapter.updateOne({id: quote.id, changes: quote}, state);
        }),
    ),
    on(QuoteActions.deleteQuoteSuccess, ((state, {command}) => {
        return adapter.removeOne(command.quote.id, state);
    })),
    on(QuotesActions.cleanUp, state => {
        return adapter.removeAll(state);
    }),
    on(QuoteActions.openAddToQuoteNav, state => ({...state, addToQuoteNav: true})),
    on(QuoteActions.closeAddToQuoteNav, state => ({...state, addToQuoteNav: false})),
    on(QuoteActions.openAddToQuoteMenu, state => ({...state, addToQuoteMenu: true})),
    on(QuoteActions.closeAddToQuoteMenu, state => ({...state, addToQuoteMenu: false})),
    on(QuoteActions.openCreateNewQuote, state => ({...state, createNewQuote: true})),
    on(QuoteActions.closeCreateNewQuote, state => ({...state, createNewQuote: false, addToQuoteMenu: true})),
    on(QuoteActions.openChooseQuote, state => ({...state, chooseQuote: true})),
    on(QuoteActions.closeChooseQuote, state => ({...state, chooseQuote: false})),
    on(QuoteActions.openQuoteListMenu, state => ({...state, quoteListMenu: true, addToQuoteMenu: false, createNewQuote: false, chooseQuote: false})),
    on(QuoteActions.closeQuoteListMenu, state => ({...state, quoteListMenu: false})),
    on(QuoteActions.changeChosenQuote, (state, {chosenQuote}) => ({...state, chosenQuote: chosenQuote})),
    on(QuoteActions.deselectNewlyCreatedQuote, (state) => ({...state, newlyCreatedQuote: null})),
    on(QuoteActions.removeChosenQuote, state => ({...state, chosenQuote: null})),

    on(QuoteActions.createQuoteSuccess, (state, {quote}) => {
        if (state.chosenQuote) {
            return ({...state, chosenQuote: quote});
        }

        return {...state, newlyCreatedQuote: quote};
    }),
    on(QuoteActions.editProductSuccess, (state, {quote}) => ({...state, chosenQuote: quote})),
    on(QuoteActions.addProductSuccess, (state, {quote}) => ({...state, chosenQuote: quote})),
    on(QuoteActions.closeAllSideNavs, state => ({...state, addToQuoteNav: false, addToQuoteMenu: false, createNewQuote: false, chooseQuote: false, quoteListMenu: false, quoteEdit: false, requestQuote: false, quoteDetail: false, newlyCreatedQuote: null})),
    on(QuoteActions.openEditQuote, state => ({...state, quoteEdit: true})),
    on(QuoteActions.closeEditQuote, state => ({...state, quoteEdit: false})),
    on(QuoteActions.openRequestQuote, state => ({...state, requestQuote: true})),
    on(QuoteActions.closeRequestQuote, state => ({...state, requestQuote: false})),
    on(QuoteActions.openQuoteDetail, state => ({...state, quoteDetail: true})),
    on(QuoteActions.closeQuoteDetail, state => ({...state, quoteDetail: false})),
    on(QuoteActions.addToQuoteQuantity, (state, {quantity}) => ({...state, selectedQuantity: quantity})),
    on(QuoteActions.requestSuccess, state => ({...state, quoteOrderComplete: true})),
    on(QuoteActions.openQuoteOrderComplete, state => ({...state, quoteOrderComplete: true})),
    on(QuoteActions.closeQuoteOrderComplete, state => ({...state, quoteOrderComplete: false})),
    on(QuoteActions.deleteQuoteSuccess, state => ({...state, chosenQuote: null, newlyCreatedQuote: null}))
);

const determinePreferredQuoteId = createReducer(
    initialState.preferredQuoteId,
    on(QuotesActions.load, QuotesActions.cleanUp, () => null),
    on(QuoteActions.addProduct, QuoteActions.editProduct, (preferredQuoteId, {command}) => {
        return command.quote.id;
    }),
    on(QuoteActions.createQuoteSuccess, (preferredQuoteId, {quote}) => {
        return quote.id;
    }),
    on(QuoteActions.deleteQuoteSuccess, (preferredQuoteId, {command}) => {
        return differentOrNull(preferredQuoteId, command.quote.id);
    })
);

const determineSelectedQuoteId = createReducer(
    initialState.selectedQuoteId,
    on(QuotesActions.load, QuotesActions.cleanUp, () => null),
    on(QuoteActions.deleteQuoteSuccess, (selectedQuoteId, {command}) => {
        return differentOrNull(selectedQuoteId, command.quote.id);
    }),
    on(QuoteActions.select, (selectedQuoteId, {command}) => {
        return getIdOrNull(command.quote);
    }),
);

const determineIsPending = createReducer(
    initialState.isPending,
    on(QuotesActions.load, QuoteActions.createQuote, () => true),
    on(
        QuotesActions.loadingSuccess,
        QuotesActions.loadingFailure,
        QuoteActions.createQuoteSuccess,
        QuoteActions.createQuoteFailure,
        QuotesActions.cleanUp, () => false),
);

const determinePendingQuoteCollection = createReducer(
    initialState.pendingQuoteCollection,
    on(QuoteActions.editQuote,
        QuoteActions.addProduct,
        QuoteActions.editProduct,
        QuoteActions.deleteQuote,
        QuoteActions.deleteProduct,
        QuoteActions.request, (pendingQuoteCollection, {command}) => {
            return {
                ...pendingQuoteCollection,
                [command.quote.id]: true
            };
        }),
    on(
        QuoteActions.editQuoteSuccess,
        QuoteActions.editQuoteFailure,
        QuoteActions.addProductSuccess,
        QuoteActions.addProductFailure,
        QuoteActions.editProductSuccess,
        QuoteActions.editProductFailure, (pendingQuoteCollection, {command}) => {
            return {
                ...pendingQuoteCollection,
                [command.quote.id]: false
            };
        }),
    on(
        QuoteActions.deleteQuoteSuccess,
        QuoteActions.deleteQuoteFailure,
        QuoteActions.deleteProductSuccess,
        QuoteActions.deleteProductFailure,
        QuoteActions.requestSuccess,
        QuoteActions.requestFailure, (pendingQuoteCollection, {command}) => {
            return {
                ...pendingQuoteCollection,
                [command.quote.id]: false
            };
        }),
    on(QuotesActions.cleanUp, () => ({})),
);

function getIdOrNull(quote: Quote) {
    if (quote == null) {
        return null;
    }
    return quote.id;
}

export const getPreferredQuoteId = (state: State) => state.preferredQuoteId;
export const getSelectedQuoteId = (state: State) => state.selectedQuoteId;
export const getIsPending = (state: State) => state.isPending;
export const getPendingQuoteCollection = (state: State) => state.pendingQuoteCollection;
export const getAddToQuoteNav = (state: State) => state.addToQuoteNav;
export const getAddToQuoteMenu = (state: State) => state.addToQuoteMenu;
export const getCreateNewQuote = (state: State) => state.createNewQuote;
export const getChooseQuote = (state: State) => state.chooseQuote;
export const getChosenQuote = (state: State) => state.chosenQuote;
export const getNewlyCreatedQuote = (state: State) => state.newlyCreatedQuote;
export const getQuoteListMenu = (state: State) => state.quoteListMenu;
export const getQuoteEdit = (state: State) => state.quoteEdit;
export const getRequestQuote = (state: State) => state.requestQuote;
export const getQuoteDetail = (state: State) => state.quoteDetail;
export const getSelectedQuantity = (state: State) => state.selectedQuantity;
export const getQuoteOrderComplete =  (state: State) => state.quoteOrderComplete;
